/**
 * The screener's Add row, the host half: what typed text means, and whether it
 * may go into the list on screen.
 *
 * The island (`@qufolio/screener-grid`'s `AddRowBinding`) draws the row and
 * reports the text; everything that needs the symbol index or the list store
 * is decided here, because the island may not import either. Pure — the index
 * rows, the list and the two effects are handed in — so every rule below is a
 * test case rather than something to click through.
 *
 * ## What a piece of text resolves to
 *
 * The **stored spelling**, which is the only spelling a list may hold, and the
 * one every other surface already files:
 *
 * - **A ticker** is `canonicalSymbol`'s form — upper case, `-` as `.`, so
 *   `brk-b` is `BRK.B`, the spelling the index and the universe use.
 * - **A bare NSE name** — `reliance` — is `RELIANCE.NS` when the index has no
 *   `RELIANCE` of its own.
 * - **A half-typed name** — `tsl`, `apple` — is the search dialog's top row for
 *   the same keystrokes: TSLA, AAPL. The owner's call (2026-09-21), reversing
 *   the first version, which refused anything but an exact ticker. The Add row
 *   highlights that row before Enter is pressed, so the pick is seen, not
 *   guessed at; this is the same rule for text that reaches here without a
 *   list — typed before the index had landed, say. An exact ticker always wins
 *   (`rankSymbols` puts it first), so `ms` is MS, not MSFT; and a top match is
 *   a row of the index by construction, so nothing unknown is ever filed.
 * - **A ratio** — `aapl/spy` — has every leg resolved the same way and is stored
 *   as the canonical composite with no spaces, `AAPL/SPY`: exactly what
 *   `SymbolDialog`'s commit writes into `ChartGroup.symbol` for that chart, so a
 *   ratio filed from the grid and one filed from the chart's star are one row.
 *
 * Nothing longer than 32 characters, the server's column.
 *
 * ## A paste is several entries, and each must be exact
 *
 * Commas, semicolons and whitespace separate; whitespace *around an operator*
 * does not, so `aapl / spy` is one ratio rather than three refusals. Each entry
 * stands alone — the good ones go in, and each bad one comes back with its own
 * sentence, which is what the row shows and the box keeps.
 *
 * **A box holding several entries is never completed**: no suggestions are
 * offered for it, and no entry of it falls back to a top match. A pasted list
 * is deliberate, and guessing at each line of it could file symbols nobody
 * chose — `AMD TSL` refuses `TSL` rather than filing TSLA beside AMD.
 */

import type { AddRowRejection, AddRowResult, AddRowSuggestion } from "@qufolio/screener-grid";
import { refusalFor, refusalMessage } from "./symbol-access";

import {
  CompositeError,
  activeToken,
  canonicalSymbol,
  isComposite,
  parseCompositeOrThrow,
  replaceActiveToken,
} from "./composite";
import { MARKETS } from "./market";
import type { SymbolRow } from "./symbol-index";
import { rankSymbols } from "./symbol-rank";
import type { TrackList } from "./track-lists";

/** `track_list_member.symbol` is `VARCHAR(32)`; a longer one is a 400 anyway. */
export const MAX_LIST_SYMBOL = 32;

/** Suggestions under the cell — a handful, since the list is a prompt, not a browser. */
export const ADD_ROW_SUGGESTIONS = 8;

/** What separates entries in a paste. Not the operators — those join. */
const SEPARATOR = /[\s,;]/;

/**
 * The list the Add row files into, or none.
 *
 * **Exactly one group on screen, and it is one of the caller's own lists.** An
 * index, a sector or a theme is not something a user adds to; and a
 * multi-select that mixes a list with SP500 is a screen of both, where a row
 * typed at the bottom would land in one of them for a reason the screen does
 * not show. A list is the caller's own when it is in the store — the store only
 * ever holds the caller's lists, since `/lists` answers per user.
 */
export function listForScreen(
  codes: readonly string[],
  lists: readonly TrackList[],
): TrackList | undefined {
  if (codes.length !== 1) return undefined;
  const code = codes[0]!.toUpperCase();
  return lists.find((list) => list.code.toUpperCase() === code);
}

/** `AMD, TSLA NVDA` as three entries; `aapl / spy` as one. */
export function splitEntries(text: string): string[] {
  return text
    .replace(/\s*([+\-*/])\s*/g, "$1")
    .split(/[\s,;]+/)
    .filter(Boolean);
}

/**
 * One ticker in its stored spelling, or `null` when the index has no such thing.
 *
 * Exact first, then with a market suffix — the order the search dialog ranks
 * them in, since an exact ticker outranks a prefix there too.
 */
function resolveTicker(raw: string, known: ReadonlySet<string>): string | null {
  const canonical = canonicalSymbol(raw);
  if (known.has(canonical)) return canonical;
  for (const market of MARKETS) {
    if (!market.suffix || canonical.endsWith(market.suffix)) continue;
    if (known.has(canonical + market.suffix)) return canonical + market.suffix;
  }
  return null;
}

/**
 * The ticker the search dialog would list first for this text, or `null`.
 *
 * The half-typed-name rule, for a single entry only — see the module header.
 * `rankSymbols` orders by tier (exact, ticker prefix, name prefix, substring),
 * so this is never a looser answer than an exact one when an exact one exists.
 */
function topMatch(raw: string, rows: readonly SymbolRow[]): string | null {
  return rankSymbols(rows, raw, 1)[0]?.symbol ?? null;
}

/** The leg the index does not hold, and the whole entry it was typed in. */
interface Outside {
  value: string;
  leg: string;
}

type Resolved =
  | { ok: true; symbol: string; single: boolean }
  | { ok: false; reason: string; outside?: Outside };

/**
 * An entry the index does not hold. The index is the user's universe (it is
 * narrowed on the server), so "not in it" means *not in your plan* or *not a
 * symbol at all*, and `addFromRow` asks which before the words are shown —
 * `lib/symbol-access.ts`, the same sentences the symbol search uses.
 */
const outside = (value: string, leg: string): Resolved => ({
  ok: false,
  reason: refusalMessage(value, leg, undefined),
  outside: { value, leg },
});

const tooLong = (symbol: string): Resolved => ({
  ok: false,
  reason: `${symbol} is longer than ${MAX_LIST_SYMBOL} characters`,
});

/**
 * One entry, as the list would store it, or the sentence that says why not.
 *
 * `guessFrom` is the index when this entry is the whole box, and `null` when it
 * is one of several: only then may a ticker that is not exact fall back to its
 * top match.
 */
function resolveEntry(
  entry: string,
  known: ReadonlySet<string>,
  guessFrom: readonly SymbolRow[] | null,
): Resolved {
  const upper = entry.toUpperCase();
  // Routed the way every other surface routes: `/`, `*`, `+` decide on sight,
  // and a lone hyphen is a ticker when the index knows one by that name. Exact
  // only — a top match must not turn `amd-tsl` from a spread into a ticker.
  const holds = (symbol: string) => resolveTicker(symbol, known) !== null;
  const resolve = (raw: string) =>
    resolveTicker(raw, known) ?? (guessFrom ? topMatch(raw, guessFrom) : null);

  if (!isComposite(upper, holds)) {
    const typed = canonicalSymbol(upper);
    if (typed.length > MAX_LIST_SYMBOL) return tooLong(typed);
    const symbol = resolve(upper);
    return symbol ? { ok: true, symbol, single: true } : outside(typed, typed);
  }

  const typed = upper.replace(/\s+/g, "");
  if (typed.length > MAX_LIST_SYMBOL) return tooLong(typed);
  let operands: string[];
  try {
    operands = parseCompositeOrThrow(typed).operands;
  } catch (error) {
    // The grammar's own sentence — "AAPL/: an operator is missing an operand".
    return { ok: false, reason: error instanceof CompositeError ? error.message : String(error) };
  }

  const legs = new Map<string, string>();
  const missing: string[] = [];
  for (const operand of operands) {
    const leg = resolve(operand);
    if (leg) legs.set(operand, leg);
    else missing.push(operand);
  }
  // The first leg that fails, named in the ratio it was typed in.
  if (missing.length > 0) return outside(typed, missing[0]!);
  // Each leg in its stored spelling — a bare NSE leg gains its suffix — with the
  // operators exactly where they were typed.
  const symbol = typed.replace(/[^+\-*/]+/g, (operand) => legs.get(operand) ?? operand);
  if (symbol.length > MAX_LIST_SYMBOL) return tooLong(symbol);
  return { ok: true, symbol, single: false };
}

export interface AddPlan {
  /** What will be sent, in stored spelling, first appearance kept. */
  symbols: string[];
  /**
   * The index's price for each single that has one — the figure the search
   * dialog's star and the chart's star record for the same symbol. **None for a
   * ratio**: nothing in the client computes a composite's value, and an
   * invented number is worse than null.
   */
  closes: Record<string, number>;
  rejected: (AddRowRejection & { outside?: Outside; already?: true })[];
}

/**
 * Everything the Add row decides before a request goes out.
 *
 * `rows` undefined means the index has not landed (or could not), and then
 * nothing can be checked — so nothing is sent. A list filed with unchecked
 * text is the failure this whole row exists to prevent.
 */
export function planAdd(
  text: string,
  {
    rows,
    list,
    exact = false,
  }: {
    rows: readonly SymbolRow[] | undefined;
    list: TrackList;
    /**
     * Never complete, even a single entry. Import's rule: a file or a paste of
     * one symbol is as deliberate as a paste of fifty.
     */
    exact?: boolean;
  },
): AddPlan {
  const entries = splitEntries(text);
  const plan: AddPlan = { symbols: [], closes: {}, rejected: [] };
  if (!rows) {
    plan.rejected = entries.map((entry) => ({
      text: entry,
      reason: `the symbol list hasn't loaded, so ${entry.toUpperCase()} can't be checked`,
    }));
    return plan;
  }

  const known = new Set(rows.map((row) => row.symbol));
  const inList = new Set(list.members.map((member) => member.symbol.toUpperCase()));
  // A half-typed name completes only when it is the whole box; a paste is exact.
  const guessFrom = entries.length === 1 && !exact ? rows : null;
  for (const entry of entries) {
    const resolved = resolveEntry(entry, known, guessFrom);
    if (!resolved.ok) {
      plan.rejected.push({ text: entry, reason: resolved.reason, outside: resolved.outside });
      continue;
    }
    const { symbol } = resolved;
    if (inList.has(symbol)) {
      plan.rejected.push({ text: entry, reason: `${symbol} is already in ${list.name}`, already: true });
      continue;
    }
    // The same symbol twice in one paste is one add, said nothing about.
    if (plan.symbols.includes(symbol)) continue;
    plan.symbols.push(symbol);
    if (!resolved.single) continue;
    const price = rows.find((row) => row.symbol === symbol)?.price;
    if (typeof price === "number" && Number.isFinite(price) && price > 0) {
      plan.closes[symbol] = price;
    }
  }
  return plan;
}

/**
 * The whole of Enter on the Add row: plan, file, and ask for the screen again.
 *
 * `refresh` runs **only after the store has acknowledged the add**, and only
 * when something went in. The grid's rows are the server's snapshot of the
 * list: the subscription re-reads the roster on its own 60-second rebuild, so
 * without asking the row would arrive up to a minute after Enter — and on the
 * single-`GET` path, never. Asked before the write is stored, the rebuild would
 * not have it. A refusal changes nothing, and a failed write is rolled back by
 * the store, so neither asks.
 */
export async function addFromRow(
  text: string,
  {
    rows,
    list,
    add,
    refresh,
    explain = refusalFor,
  }: {
    rows: readonly SymbolRow[] | undefined;
    list: TrackList;
    add: (listId: string, symbols: readonly string[], closes: Record<string, number>) => Promise<void>;
    refresh: () => void;
    /**
     * The sentence for a symbol the index does not hold — which asks the
     * server whether it exists at all. Handed in like `add`, so this stays a
     * function a test can drive without a network.
     */
    explain?: (value: string, leg: string) => Promise<string>;
  },
): Promise<AddRowResult> {
  const plan = planAdd(text, { rows, list });
  // Only on the refusal path: which of the two sentences an unheld symbol gets
  // depends on whether it exists at all, which the narrowed index cannot say.
  // Awaited only when there is something to explain, so a clean add still
  // reaches the store in the same tick as Enter.
  const unheld = plan.rejected.filter((rejection) => rejection.outside);
  if (unheld.length > 0) {
    await Promise.all(
      unheld.map(async (rejection) => {
        rejection.reason = await explain(rejection.outside!.value, rejection.outside!.leg);
      }),
    );
  }
  if (plan.symbols.length === 0) return { added: [], rejected: plan.rejected };
  try {
    await add(list.id, plan.symbols, plan.closes);
  } catch (error) {
    // The server's own sentence, against each symbol it refused — the store
    // has already put the list back as it was.
    const why = error instanceof Error ? error.message : String(error);
    return {
      added: [],
      rejected: [
        ...plan.rejected,
        ...plan.symbols.map((symbol) => ({ text: symbol, reason: `${symbol} wasn't added: ${why}` })),
      ],
    };
  }
  refresh();
  return { added: plan.symbols, rejected: plan.rejected };
}

/** The entry the caret is in, and where it starts and ends in the box. */
function entryAt(text: string, caret: number): { start: number; end: number; value: string } {
  const at = Math.max(0, Math.min(caret, text.length));
  let start = at;
  while (start > 0 && !SEPARATOR.test(text[start - 1]!)) start -= 1;
  let end = at;
  while (end < text.length && !SEPARATOR.test(text[end]!)) end += 1;
  return { start, end, value: text.slice(start, end) };
}

/**
 * Completions for the box, ranked as the search dialog ranks them.
 *
 * On the **operand under the caret**: `aapl/sp` lists SPY, as `SymbolDialog`
 * does for `AAPL/SP` — ranking the whole string would find nothing. Each
 * suggestion carries the whole box as it would be with that operand replaced,
 * so accepting one files the expression, not the leg.
 *
 * **The island highlights the first of these and Enter files it**, which is
 * what makes two rules here load-bearing: `rankSymbols` puts an exact ticker
 * first (`ms` offers MS, then MSFT), and a box holding **more than one entry is
 * offered nothing** — a paste is submitted as typed and checked entry by entry,
 * never completed. See the module header.
 */
export function addRowSuggestions(
  rows: readonly SymbolRow[] | undefined,
  text: string,
  caret: number,
  limit: number = ADD_ROW_SUGGESTIONS,
): AddRowSuggestion[] {
  if (!rows) return [];
  if (splitEntries(text).length > 1) return [];
  const entry = entryAt(text, caret);
  const within = caret - entry.start;
  const needle = activeToken(entry.value, within).value.trim();
  if (!needle) return [];
  return rankSymbols(rows, needle, limit).map((row) => ({
    symbol: row.symbol,
    name: row.name,
    value:
      text.slice(0, entry.start) +
      replaceActiveToken(entry.value, within, row.symbol).text +
      text.slice(entry.end),
  }));
}
